/*
 * Copyright (c) 2023. American Express Company
 */

// third-party
import React from 'react';
import { IconAccount } from '@americanexpress/dls-react';

// models and services
import { DLSSize } from 'src/types/dls-size.enum';
import LiteralsService from 'src/services/literals/LiteralsService';
import MixpanelService from 'src/services/mixpanel.service';
import { PersonModel } from 'src/types/person.model';
import PersonService from 'src/services/person.service';
import { WhoelseViewModel } from 'src/types/view-models/onboarding/whoelse.view.model';

// components
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import PersonDialog from 'src/components/person-dialog/PersonDialog';
import WideView from 'src/components/atomic/layouts/wideView/WideView';
import ProfilePersonCard from 'src/components/card/ProfilePersonCard';
import OptionsBox from 'src/components/options-box/OptionsBox';
import ImageUpload from 'src/components/image-upload/ImageUpload';

// icons and styles
import styles from './Whoelse.module.scss';
import OnboardingButtons from 'src/components/onboarding-buttons/OnboardingButtons';

export default function Whoelse({
    currentPerson,
    openPersonDialog,
    updatePersons,
    user,
    updateUserLocally,
    updatePerson,
    setState,
    persons,
    startAddPerson,
    back,
    whoElseNext
}: WhoelseViewModel): JSX.Element {
    const title: string = LiteralsService.getLiteral('Onboarding.whoelse.title');
    const subtitleElement: JSX.Element = (
        <Typography variant={TypographyVariants.BODY} className={styles.subtitleElement}>
            {LiteralsService.getLiteral('Onboarding.whoelse.tabDesc')}
        </Typography>
    );

    /*     const startUpload = (): void => {
        // pick an image unless already picking an image
        if (!$('.image-crop-dialog')[0]) {
            $(`${styles?.imageUploader ? ` .${styles.imageUploader}` : ''} .imageUpload`).click();
        }
    }; */

    const handlePersonUpdate = async (blob: Blob): Promise<void> => {
        if (currentPerson.relationship) {
            MixpanelService.track('o4 Photo Upload: ' + currentPerson.relationship);
        }

        const person: PersonModel = currentPerson;
        person.photo = blob;
        updatePerson(person);
        if (person.personUid) {
            await PersonService.updatePerson(person);
        }
        await updatePersons();
    };

    return (
        <WideView
            title={title}
            subtitleElement={subtitleElement}
            classNameView="onboarding-drawer whoElseView"
            hideBackButton
            hasBackgroundBanner
            showFooter
        >
            <div className={styles.whoElseView}>
                {currentPerson && openPersonDialog && (
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    <PersonDialog
                        updatePersons={updatePersons}
                        person={currentPerson}
                        user={user}
                        updateUserLocally={updateUserLocally}
                        open={openPersonDialog}
                        updatePerson={updatePerson}
                        close={() => {
                            setState({
                                openPersonDialog: false
                            });
                        }}
                    />
                )}

                <div className={styles.contentArea}>
                    {persons
                        ? persons.map((person: PersonModel, index: number) => {
                              if (index === 0) {
                                  return (
                                      <ImageUpload
                                          className={styles.imageUploader}
                                          customRender
                                          imageUploadId={`imageUpload-person-${index}`}
                                          key={`imageUpload-person-${index}`}
                                          onCancel={() => {
                                              setState({ openImageUpload: false });
                                          }}
                                          onSubmit={async (blob: Blob) => {
                                              await handlePersonUpdate(blob);
                                          }}
                                          roundedPreview
                                      >
                                          <ProfilePersonCard
                                              person={person}
                                              key={`person-${index}`}
                                              personText={
                                                  ' ' /* LiteralsService.getLiteral('Onboarding.whoelse.addUpdatePhoto', [person.photos.length > 0]) */
                                              }
                                              onClick={() => {
                                                  // startUpload();
                                                  setState({ currentPerson: person });
                                              }}
                                          />
                                      </ImageUpload>
                                  );
                              } else {
                                  return (
                                      <ProfilePersonCard
                                          person={person}
                                          key={`person-${index}`}
                                          onClick={(person: any) => {
                                              setState({
                                                  currentPerson: person,
                                                  openPersonDialog: true
                                              });
                                          }}
                                      />
                                  );
                              }
                          })
                        : null}
                    <OptionsBox
                        headerText={LiteralsService.getLiteral('Onboarding.whoelse.addFamilyHeader')}
                        content={[LiteralsService.getLiteral('Onboarding.whoelse.addFamilyContent')]}
                        icon={<IconAccount size={DLSSize.MEDIUM} />}
                        onClick={() => {
                            startAddPerson();
                        }}
                        buttonText={LiteralsService.getLiteral('Onboarding.whoelse.addFamilyButton')}
                        buttonStyle={styles.optionsBoxButton}
                    />
                </div>
                <OnboardingButtons
                    back={back}
                    backButtonEventName={'o4 Who Else Back'}
                    next={whoElseNext}
                    nextButtonTitle={LiteralsService.getLiteral('Onboarding.whoelse.next')}
                    isWideView
                />
            </div>
        </WideView>
    );
}
